import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Link as LinkIcon, List, CalendarDays, Trash2, ArrowRight } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { Navigation } from '../components/Navigation';
import { RescheduleRequests } from '../components/RescheduleRequests';
import { RequestHistory } from '../components/RequestHistory';
import { ScheduleCalendar } from '../components/ScheduleCalendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Schedule() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem('schedule_view') || 'list');
  const navigate = useNavigate();

  const changeView = (v) => {
    setView(v);
    localStorage.setItem('schedule_view', v);
  };

  const [bookingSlug, setBookingSlug] = useState(null);

  const bookingLink = bookingSlug
    ? `${process.env.REACT_APP_BACKEND_URL.replace('/api', '')}/book/${bookingSlug}`
    : null;

  useEffect(() => {
    fetch(`${API}/settings`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setBookingSlug(data?.booking_slug || ''))
      .catch(() => setBookingSlug(''));
  }, []);

  const fetchMeetings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/meetings`, {
        withCredentials: true,
      });
      setMeetings(data.sort((a, b) => new Date(a.date) - new Date(b.date)));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const copyBookingLink = () => {
    navigator.clipboard.writeText(bookingLink);
  };

  const handleDeleteMeeting = async (meeting, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete meeting "${meeting.title}" for ${meeting.client_name}?`)) return;
    try {
      await axios.delete(`${API}/meetings/${meeting.id}`, { withCredentials: true });
      setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));
    } catch (err) {
      alert('Failed to delete meeting. Please try again.');
      if (process.env.NODE_ENV === 'development') console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="max-w-2xl mx-auto p-4">
          <div className="text-text-secondary">Loading...</div>
        </div>
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-1" data-testid="schedule-title">
              Schedule
            </h1>
            <p className="text-text-secondary">{meetings.length} meetings</p>
          </div>
          <button
            onClick={() => navigate('/meetings/new')}
            className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 flex items-center gap-2 transition-colors"
            data-testid="add-meeting-button"
          >
            <Plus className="w-4 h-4" />
            Book
          </button>
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-xl p-4" data-testid="booking-link-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <LinkIcon className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground mb-1">Your public booking link</h3>
              <p className="text-sm text-text-secondary mb-2">Share it so clients can book themselves</p>
              {bookingLink ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={bookingLink}
                    readOnly
                    className="flex-1 bg-background/50 border border-border rounded px-3 py-1.5 text-sm text-foreground"
                    data-testid="booking-link-input"
                  />
                  <button
                    onClick={copyBookingLink}
                    className="bg-primary text-background hover:bg-primary/90 rounded px-3 py-1.5 text-sm font-medium transition-colors"
                    data-testid="copy-booking-link"
                  >
                    Copy
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/settings/booking-widget')}
                  className="text-sm text-primary hover:text-primary/80 underline underline-offset-2 transition-colors flex items-center gap-1"
                  data-testid="booking-link-no-slug"
                >
                  Set up your booking page in Settings
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <RescheduleRequests onChanged={fetchMeetings} />

        <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-1 w-fit" data-testid="schedule-view-toggle">
          <button
            onClick={() => changeView('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === 'list' ? 'bg-primary text-background' : 'text-text-secondary hover:text-foreground'
            }`}
            data-testid="view-list-btn"
          >
            <List className="w-4 h-4" />
            List
          </button>
          <button
            onClick={() => changeView('calendar')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === 'calendar' ? 'bg-primary text-background' : 'text-text-secondary hover:text-foreground'
            }`}
            data-testid="view-calendar-btn"
          >
            <CalendarDays className="w-4 h-4" />
            Calendar
          </button>
        </div>

        {view === 'calendar' ? (
          <ScheduleCalendar
            meetings={meetings}
            onMeetingClick={(meeting) => navigate(`/clients/${meeting.client_id}`)}
          />
        ) : meetings.length === 0 ? (
          <div className="bg-surface border border-border rounded-xl p-8 text-center" data-testid="schedule-empty">
            <p className="text-text-secondary mb-4">No meetings scheduled</p>
            <button
              onClick={() => navigate('/meetings/new')}
              className="bg-primary text-background hover:bg-primary/90 font-medium rounded-lg px-4 py-2 transition-colors"
            >
              Book Your First Meeting
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {meetings.map((meeting) => (
              <div
                key={meeting.id}
                className="bg-surface border border-border hover:border-primary/50 rounded-xl transition-all group"
                data-testid={`meeting-card-${meeting.id}`}
              >
                <div className="flex items-start">
                  <button
                    onClick={() => navigate(`/clients/${meeting.client_id}`)}
                    className="flex-1 text-left p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-foreground">{meeting.title}</h3>
                        {meeting.booked_by_client && (
                          <span
                            className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full border border-primary/20"
                            data-testid="self-booked-badge"
                          >
                            self-booked
                          </span>
                        )}
                      </div>
                      <StatusBadge status={meeting.status} />
                    </div>
                    <p className="text-sm text-text-secondary mb-1">{meeting.client_name}</p>
                    <p className="text-xs text-text-muted">
                      {meeting.date} {meeting.time && `at ${meeting.time}`}
                    </p>
                  </button>
                  <button
                    onClick={(e) => handleDeleteMeeting(meeting, e)}
                    className="p-3 mr-1 mt-1 text-text-muted hover:text-alert hover:bg-alert/10 rounded-lg transition-colors"
                    aria-label={`Delete meeting ${meeting.title}`}
                    data-testid={`delete-meeting-${meeting.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <RequestHistory />
      </div>
      <Navigation />
    </div>
  );
}
